/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useRef, useState } from "react";
import { gptTestService } from "../../../../../service/gptTestServices";
import { t } from "i18next";


interface QualityAssuranceVerdictProps {
  qaRemarks: string;
  setQaRemarks: React.Dispatch<React.SetStateAction<string>>;

  canSubmit: boolean;
  handleAction: (action: 'APPROVED' | 'REJECTED') => void;
  errors: any;
  selectedFile: File | null;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;

  uploadedFileId: number | null;
  setUploadedFileId: React.Dispatch<React.SetStateAction<number | null>>;
  setErrors: React.Dispatch<React.SetStateAction<any>>;
}

const QualityAssuranceVerdict: React.FC<QualityAssuranceVerdictProps> = ({
    qaRemarks,
    setQaRemarks,
    canSubmit,
    handleAction,
    errors,
    setErrors,

    selectedFile,
    setSelectedFile,
    uploadedFileId,
    setUploadedFileId,
  }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadError, setUploadError] = useState("");


  const handleFileUpload = async (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {

      const file = e.target.files?.[0];

      if (!file) return;
      setUploadError("");

      try {
        const response = await gptTestService.uploadGptEvidenceFile(file);

        if (response?.status && response?.statusCode === 200) {
          setSelectedFile(file);
          setUploadedFileId(response?.file_id);

          setErrors((prev: any) => ({
            ...prev,
            file: "",
          }));
        }
        else {
          setUploadError(response?.message || "File upload failed");
        }

      } catch (error) {
        console.error(error);
      }
    };

  return (
    <>
    <section className="bg-white rounded-[1rem] border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-500 delay-200">
             <div className="px-6 py-2 border-b border-slate-100 bg-slate-50/30 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-xl bg-[#2D3E50] text-white flex items-center justify-center text-sm font-black shadow-sm">04</div>
                <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-[0.2em]">{t("gptTest.gptTestDetails.QAVerdict")}</h3>
              </div>
            </div>
            <div className="pt-2 px-4 pb-3 grid grid-cols-1 lg:grid-cols-12 gap-5">
              <div className="col-span-6 lg:col-span-4 space-y-3">
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block px-2">{t("gptTest.gptTestDetails.evidenceRepository")} <span className="text-red-500">*</span></label>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className={`h-40 border-2 border-dashed rounded-[1rem] flex flex-col items-center justify-center gap-5 transition-all duration-500 cursor-pointer group ${selectedFile ? 'border-emerald-500 bg-emerald-50/30' : 'border-slate-200 bg-slate-50 hover:border-[#FF7043] hover:bg-white'}`}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleFileUpload}
                  />
                  <div className={`w-20 h-18 rounded-[1rem] flex items-center justify-center gap-3 transition-all duration-500 ${
                    selectedFile
                      ? 'bg-emerald-100 text-emerald-600 shadow-lg'
                      : 'bg-white text-slate-200 group-hover:text-orange-500 shadow-md group-hover:shadow-orange-500/10'
                  }`}>
                    <span className="material-symbols-outlined text-3xl">
                      {selectedFile ? 'check_circle' : 'cloud_upload'}
                    </span>
                  </div>

                  <div className="text-center px-10">
                    <p className={`text-[12px] font-black uppercase tracking-tight ${
                      selectedFile
                        ? 'text-emerald-700'
                        : 'text-slate-500 group-hover:text-slate-800'
                    }`}>
                      {selectedFile
                        ? 'CULTURE REPORT LINKED'
                        : 'Upload GPT Culture Report'}
                    </p>

                    <p className="text-[10px] text-slate-400 font-medium italic mt-2 uppercase tracking-tighter">
                      {t("gptTest.gptTestDetails.fileSupport")}
                    </p>
                    {uploadError && (
                      <div className="flex items-center justify-center gap-2 mt-4">
                        <span className="material-symbols-outlined text-red-500 text-[18px]">
                          {t("gptTest.icons.error")}
                        </span>

                        <p className="text-[11px] font-bold text-red-500">
                          {uploadError}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                 {errors.file && <p className="mt-2 text-[11px] text-red-500">{errors.file}</p>}
              </div>

              <div className="col-span-12 lg:col-span-8 space-y-2.5">
                <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest block px-2">{t("gptTest.gptTestDetails.qaRemarks")} <span className="text-red-500">*</span></label>
                <textarea 
                  value={qaRemarks}
                  onChange={(e) => {
                    setQaRemarks(e.target.value);
                    setErrors((prev: any) => ({ ...prev, qaRemarks: "" }));
                  }}
                  placeholder={t('gptTest.gptTestDetails.qaPlaceholder')}
                  className="w-full h-40 bg-slate-50 border border-slate-200 rounded-[1rem] p-10 text-sm font-medium text-slate-700 outline-none focus:bg-white focus:border-[#FF7043] focus:ring-12 focus:ring-orange-500/5 transition-all shadow-inner placeholder:text-slate-300 leading-relaxed"
                ></textarea>
                {errors.qaRemarks && <p className="mt-2 text-[11px] text-red-500">{errors.qaRemarks}</p>}
              </div>
            </div>

            <div className="px-16 py-5 bg-slate-50/50 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-10">
              <div className="flex items-center gap-5 text-emerald-600 bg-white px-8 py-3 rounded-[2rem] border-2 border-emerald-50 shadow-sm">
                 <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center">
                   <span className="material-symbols-outlined text-xl font-black">{t("gptTest.icons.lock")}</span>
                 </div>
                 <div className="flex flex-col">
                   <span className="text-[11px] font-black uppercase tracking-[0.2em]">{t("gptTest.gptTestDetails.confirmModalDesc")}</span>
                   <span className="text-[12px] font-bold opacity-80 italic">{t("gptTest.gptTestDetails.confirmModaldesc2")}</span>
                 </div>
              </div>
              <div className="flex gap-3 w-full md:w-auto">
                 <button
                    onClick={() => handleAction('REJECTED')}
                    className="flex-1 md:flex-none h-11 px-14 bg-white border border-red-500 text-red-500 rounded-full font-black text-[11px] uppercase tracking-[0.25em] hover:bg-red-50 transition-all focus:outline-none focus:ring-0"
                  >
                    {t("gptTest.gptTestDetails.rejectBatch")}
                  </button>

                  <button
                    onClick={() => handleAction('APPROVED')}
                    className="flex-1 md:flex-none px-15 py-2 bg-[#10B981] text-white rounded-[2rem] font-black text-[11px] uppercase tracking-[0.25em] hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2 shadow-none focus:outline-none focus:ring-0 focus:ring-transparent active:outline-none active:ring-0 border-none"
                  >
                    <span className="material-symbols-outlined text-lg">{t("gptTest.icons.verified_user")}</span>
                    {t("gptTest.gptTestDetails.approveAndRelease")}
                  </button>
              </div>
            </div>
          </section>
    </>
  );
};

export default QualityAssuranceVerdict;